import React, { useState, useEffect } from "react";
import apiClient from "../../../AppClient";
import { Container, Form, Button, Table, Alert, Spinner, Modal } from "react-bootstrap";

const PurchasesReport = () => {
  const [purchases, setPurchases] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");

  // Fetch all purchases
  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/transactions/purchases/");
      setPurchases(response.data);
      setError(null);
    } catch (error) {
      setError(error.response?.data?.detail || "فشل في جلب المشتريات.");
    } finally {
      setLoading(false);
    }
  };

  // Filter purchases based on date and supplier
  const filteredPurchases = purchases.filter((purchase) => {
    const matchesDate = dateFilter 
      ? purchase.invoice_date.includes(dateFilter) 
      : true;
    
    const matchesSupplier = supplierFilter 
      ? purchase.supplier.toLowerCase().includes(supplierFilter.toLowerCase()) 
      : true;
    
    return matchesDate && matchesSupplier;
  });

  // Calculate total purchase value
  const totalPurchases = filteredPurchases.reduce(
    (total, purchase) => total + parseFloat(purchase.total_price),
    0
  ).toFixed(2);

  // Open details modal
  const viewPurchaseDetails = (purchase) => {
    setSelectedPurchase(purchase);
    setShowDetails(true);
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  return (
    <Container style={{ 
      direction: "rtl", 
      textAlign: "right", 
      padding: "20px", 
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      color: "black", 
      backgroundColor: "white" 
    }}>
      <h1 style={{ color: "darkgreen", marginBottom: "20px" }}>تقرير المشتريات</h1>

      {/* Filters */}
      <div style={{ display: "flex", marginBottom: "20px", gap: "10px" }}>
        <Form.Control
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          style={{ maxWidth: "250px", color: dateFilter ? "#000" : "#aaa" }}
        />

        <Form.Control
          type="text"
          placeholder="الترتيب حسب اسم المورد"
          value={supplierFilter}
          onChange={(e) => setSupplierFilter(e.target.value)}
          style={{ maxWidth: "250px" }}
        />
        <Button 
          variant="outline-secondary" 
          onClick={() => {
            setDateFilter("");
            setSupplierFilter("");
          }}
          style={{ border: "1px solid darkgreen", color: "darkgreen" }}
        >
          مسح الترتيب
        </Button>
      </div>

      {/* Summary Card */}
      <div style={{ 
        backgroundColor: "white", 
        padding: "15px", 
        marginBottom: "20px", 
        borderRadius: "5px",
        border: "1px solid darkgreen"
      }}>
        <h4 style={{ color: "darkgreen" }}>الملخص</h4>
        <p style={{ fontSize: "18px" }}>
          <strong>إجمالي المشتريات:</strong> ${totalPurchases}
        </p>
        <p>
          <strong>عدد المعاملات:</strong> {filteredPurchases.length}
        </p>
      </div>

      {/* Error Message */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status" style={{ color: "darkgreen" }}>
            <span className="visually-hidden">جاري التحميل...</span>
          </Spinner>
        </div>
      ) : (
        /* Purchases Table */
        <Table hover responsive>
          <thead style={{ backgroundColor: "darkgreen", color: "white" }}>
            <tr>
              <th>#</th>
              <th>اسم المورد</th>
              <th>رقم الفاتورة</th>
              <th>المبلغ الكلي</th>
              <th>التاريخ</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {filteredPurchases.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">لم يتم العثور على مشتريات</td>
              </tr>
            ) : (
              filteredPurchases.map((purchase, index) => (
                <tr key={purchase.id}>
                  <td>{index + 1}</td>
                  <td>{purchase.supplier}</td>
                  <td>{purchase.invoice_number}</td>
                  <td>${parseFloat(purchase.total_price).toFixed(2)}</td>
                  <td>{purchase.invoice_date}</td>
                  <td>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => viewPurchaseDetails(purchase)}
                      style={{ border: "1px solid darkgreen", color: "darkgreen" }}
                    >
                      عرض التفاصيل
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* Details Modal */}
      <Modal show={showDetails} onHide={() => setShowDetails(false)}>
        <Modal.Header closeButton style={{ backgroundColor: "darkgreen", color: "white" }}>
          <Modal.Title>تفاصيل الفاتورة</Modal.Title>
        </Modal.Header>
        {selectedPurchase && (
          <Modal.Body>
            <p><strong>المعرف:</strong> {selectedPurchase.id}</p>
            <p><strong>اسم المورد:</strong> {selectedPurchase.supplier}</p>
            <p><strong>رقم الفاتورة:</strong> {selectedPurchase.invoice_number}</p>
            <p><strong>المبلغ الكلي:</strong> ${parseFloat(selectedPurchase.total_price).toFixed(2)}</p>
            <p><strong>عدد الأقساط:</strong> {selectedPurchase.number_of_installments}</p>
            <p><strong>طريقة الدفع:</strong> {selectedPurchase.payment_method}</p>
            <p><strong>تاريخ الفاتورة:</strong> {selectedPurchase.invoice_date}</p>
            <p><strong>الموظف المسؤول:</strong> {selectedPurchase.staff}</p>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDetails(false)}
            style={{ backgroundColor: "darkgreen", borderColor: "darkgreen" }}
          >
            إغلاق
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PurchasesReport;
